import { recordHook } from '@flatfile/plugin-record-hook'
import { validateTaxId, findUSState, parseDateFromString } from 'utils'
import { format, isValid, isBefore } from 'date-fns'

const invalidPostalCodes = [
  '01234',
  '34567',
  '56789',
  '67890',
  '00000',
  '11111',
  '22222',
  '33333',
  '55555',
  '66666',
  '77777',
  '99999',
]

const isNumeric = (value) => value.replace(/\D/g, '') === value
const isNa = (value) => {
  const isString = typeof value === 'string'
  const formattedValue = value.toUpperCase()
  const matchesNa = formattedValue === 'N/A'
  return isString && matchesNa
}

/* The folowings are product specific formattings and validations
  any formatting or validation specific to Search Services should be added here */

export const stateFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const state = record.get('state')
      if (state) {
        const matchingState = findUSState(state)
        if (matchingState) {
          record.set('state', matchingState.value)
        } else {
          record.addError('state', 'Invalid US State abbreviation')
        }
      }
      return record
    })
  )
}

export const dobFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('dob')
      if (dob) {
        const dobDate = isNumeric(dob) ? null : parseDateFromString(dob)
        let formattedDob = dob

        if (dobDate) {
          formattedDob = format(dobDate, 'M/d/yyyy')
          record.set('dob', formattedDob)
        } else {
          const sanitizedDob = dob.match(/[0-9\\/]/g)?.join('')
          if (sanitizedDob && sanitizedDob !== '/') {
            formattedDob = sanitizedDob
            record.set('dob', formattedDob)
          } else {
            formattedDob = isNa(dob) ? dob.toUpperCase() : dob
            record.set('dob', formattedDob)
          }
        }
      }
      return record
    })
  )
}

export const dodFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dod = record.get('dod')
      if (dod) {
        const dodDate = isNumeric(dod) ? null : parseDateFromString(dod)
        let formattedDod = dod

        if (dodDate) {
          formattedDod = format(dodDate, 'M/d/yyyy')
          record.set('dod', formattedDod)
        } else {
          const sanitizedDod = dod.match(/[0-9\\/]/g)?.join('')
          if (sanitizedDod && sanitizedDod !== '/') {
            formattedDod = sanitizedDod
            record.set('dod', formattedDod)
          } else {
            formattedDod = isNa(dod) ? dod.toUpperCase() : dod
            record.set('dod', formattedDod)
          }
        }
      }
      return record
    })
  )
}

export const ssnValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const ssn = record.get('ssn')
      if (ssn) {
        if (!validateTaxId(ssn)) {
          record.addError('ssn', 'Given SSN is not valid')
        }
      }
      return record
    })
  )
}

export const dobValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dob = record.get('dob')
      if (dob) {
        const dobDate = isNumeric(dob) ? null : new Date(dob)

        if (isValid(dobDate)) {
          const currentDate = new Date()
          const maxDate = new Date(currentDate)
          maxDate.setFullYear(currentDate.getFullYear() - 100)
          const minDate = new Date(currentDate)
          minDate.setFullYear(currentDate.getFullYear() - 10)
          if (dobDate > minDate) {
            record.addError('dob', 'Must be over 10 years.')
          } else {
            if (isBefore(dobDate, maxDate)) {
              record.addError('dob', 'Must be under 100 years.')
            }
          }
        } else {
          if (!isNa(dob)) {
            const dateRe = new RegExp('^[0-9]{1,2}/[0-9]{1,2}/[0-9]{4}$')
            dateRe.test(dob)
              ? record.addError('dob', 'Must be a valid date')
              : record.addError(
                  'dob',
                  'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
                )
          }
        }
      }
      return record
    })
  )
}

export const dodValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dod = record.get('dod')
      if (dod) {
        const dodDate = isNumeric(dod) ? null : new Date(dod)

        if (isValid(dodDate)) {
          if (dodDate > new Date()) {
            record.addError('dod', 'Cannot be in the future')
          } else {
            if (isBefore(dodDate, new Date(0, 0, 1))) {
              record.addError('dod', 'Must be more recent than 1900')
            }
          }
        } else {
          if (!isNa(dod)) {
            const dateRe = new RegExp('^[0-9]{1,2}/[0-9]{1,2}/[0-9]{4}$')
            dateRe.test(dod)
              ? record.addError('dod', 'Must be a valid date')
              : record.addError(
                  'dod',
                  'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
                )
          }
        }
      }
      return record
    })
  )
}

export const zipCodeValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const zip = record.get('zipCode')
      if (zip) {
        const zipRe = new RegExp('^[0-9]{5}(?:-[0-9]{4})?$')
        const postalCode = zip.substring(0, 5)
        if (!zipRe.test(zip)) {
          record.addError(
            'zipCode',
            'Zip must have format: XXXXX or XXXXX-XXXX'
          )
        } else if (invalidPostalCodes.includes(postalCode)) {
          record.addError('zipCode', 'Please enter a valid Zip Code')
        }
      }
      return record
    })
  )
}
